import React, { useEffect, useState } from 'react';
import {
  Page,
  Popup,
  Navbar,
  Row,
  Col,
  Preloader,
  Button
} from 'framework7-react';
import { gae } from '../../helpers/Analytics';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import PaperflyAPI from '../../../server/api/PaperflyAPI';
import {
  initWebSocket,
  subscribeWebSocket,
  unSubscribeWebSocket
} from '../../../redux/actions/websocket';
import {
  WEBSOCKET_STATUS,
  WEBSOCKET_TOPICS
} from '../../../redux/middleware/webSocket';
import { startCronJob, stopCronJob } from '../../../redux/actions/cronJobs';
import QRCode from 'qrcode.react';
import { getCurrentHost, REQUEST_TYPE } from '../../../lib/utils';
import II18 from '../../translate/II18';
import {
  beginJourney,
  endJourney,
  JOURNEY,
  getHandoffParentSpanContext
} from '../../../telemetry/tracing';
import { withTraceparent } from '../../../telemetry/traceparent';
import { forceFlushTelemetry } from '../../../telemetry/otel';

export default function SignOnMobilePopUp(props) {
  const {
    opened,
    onClose,
    onDone,
    documentName,
    widgetTitle,
    collectBioData
  } = props;

  const dispatch = useDispatch();

  const [checkingState, setCheckingState] = useState(false);
  const [signId, setSignId] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [touchData, setTouchData] = useState('');
  const [errorOnCreateSignOnMobile, setErrorOnCreateSignOnMobile] = useState(
    false
  );

  const signedWidgets = useSelector(state => state.receivedMobileSignatures);
  const wsStatus = useSelector(state => state.wsStatus);

  const stopCheckingSignature = () => {
    setCheckingState(false);

    dispatch(stopCronJob(signId));
    dispatch(unSubscribeWebSocket(WEBSOCKET_TOPICS.SIGN_ON_MOBILE));
  };

  const checkSignature = signId => {
    // Return promise so cron poll-quiet covers the async XHR span.
    return PaperflyAPI.getSignOnMobileStatus(signId).then(
      response => {
        if (response.mobileSignature && response.mobileSignature.signature) {
          doOnReceiveSignatire(
            response.mobileSignature.signature,
            response.mobileSignature.biometricData
          );
        }
      },
      error => {
        //nothing to do
      }
    );
  };

  const startCheckingSignatureWS = signId => {
    dispatch(subscribeWebSocket(WEBSOCKET_TOPICS.SIGN_ON_MOBILE, signId));
  };

  const startCheckingSignature = signId => {
    if (checkingState) return;

    gae('SIGN_ON_MOBILE_POP_UP', 'START_CRONJOB');

    setCheckingState(true);

    dispatch(
      startCronJob(signId, 2000, function() {
        return checkSignature(signId);
      })
    );
  };

  const doOnReceiveSignatire = (data, touchData) => {
    gae('SIGN_ON_MOBILE_POP_UP', 'SUCCESSFULY_SIGNED');
    stopCheckingSignature();
    setSignatureData(data);
    setTouchData(touchData);
    endJourney(JOURNEY.HANDOFF, 'ok', { 'handoff.result': 'signed' });
  };

  useEffect(() => {
    if (opened && signId) {
      const signInfo = signedWidgets.get(signId);
      if (signInfo) {
        doOnReceiveSignatire(signInfo.signature, signInfo.biometricData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedWidgets]);

  useEffect(() => {
    if (opened && signId && wsStatus === WEBSOCKET_STATUS.CONNECTED) {
      startCheckingSignatureWS(signId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signId, wsStatus]);

  useEffect(() => {
    if (opened && !signId) {
      dispatch(initWebSocket());
      beginJourney(JOURNEY.HANDOFF, 'handoff.sign_on_mobile', {
        'handoff.role': 'host',
        'handoff.collect_bio': Boolean(collectBioData)
      });

      PaperflyAPI.signOnMobile(documentName, widgetTitle).then(
        response => {
          if (response.mobileSignatureId) {
            setSignId(response.mobileSignatureId);
            startCheckingSignature(response.mobileSignatureId);
            forceFlushTelemetry();
          } else {
            setErrorOnCreateSignOnMobile(true);
            endJourney(JOURNEY.HANDOFF, 'error', 'create_failed');
          }
        },
        error => {
          setErrorOnCreateSignOnMobile(true);
          endJourney(JOURNEY.HANDOFF, 'error', 'create_failed');
        }
      );
    } else if (!opened) {
      setSignId('');
      setSignatureData('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const onPopUpClose = () => {
    gae('SIGN_ON_MOBILE_POP_UP', 'CLOSE');
    stopCheckingSignature();
    endJourney(JOURNEY.HANDOFF, 'ok', { 'handoff.result': 'closed' });
    onClose();
  };

  // ETE: data-testid is appended to pwaDomain + "s=" — must stay "id[&params…]".
  const signIdWithOtel = withTraceparent(signId, getHandoffParentSpanContext());
  const signUrl =
    getCurrentHost() +
    REQUEST_TYPE.SIGN_NOW +
    signIdWithOtel +
    (collectBioData ? '&touchData=true' : '');
  return (
    <Popup
      id={'sign_on_mobile_popup'}
      className="sign_on_mobile_popup"
      animate={false}
      opened={Boolean(opened)}
      swipeToClose={false}
      closeByOutsideClick={false}
      closeByBackdropClick={false}
      onPopupClosed={onPopUpClose}
      onPopupOpen={() => gae('SIGN_ON_MOBILE_POP_UP', 'SHOW')}
      style={{ height: 'auto', marginTop: -215 }}
    >
      <Page style={{ background: '#fff', position: 'relative' }}>
        <Navbar title={II18.get('title_sign_on_mobile')}></Navbar>

        <div
          id={'sign_on_mobile_code'}
          data-testid={signIdWithOtel || signId}
        />
        <Row style={{ minHeight: 230, alignItems: 'center', margin: '0 20px' }}>
          <Col>
            {signatureData ? (
              <Row style={{ alignItems: 'center' }}>
                <Col style={{ height: '100%', textAlign: 'center' }}>
                  <img
                    alt={'Signature'}
                    src={signatureData}
                    style={{ maxHeight: '100%', maxWidth: '100%' }}
                  />
                </Col>
              </Row>
            ) : signId ? (
              <Row style={{ alignItems: 'center' }}>
                <Col style={{ height: '100%', textAlign: 'center' }}>
                  <p
                    style={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      marginBottom: 40
                    }}
                  >
                    <II18>sign_on_mobile_info</II18>
                  </p>
                  <QRCode
                    size={120}
                    fgColor={'#333'}
                    level="L"
                    value={signUrl}
                  />
                </Col>
              </Row>
            ) : (
              <Row>
                <Col style={{ height: '100%', textAlign: 'center' }}>
                  {errorOnCreateSignOnMobile ? (
                    <p>
                      <II18>technical_error</II18>
                    </p>
                  ) : (
                    <Preloader size={62} />
                  )}
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            marginTop: 30,
            padding: 5,
            borderTop: '1px solid #eee'
          }}
        >
          <Button onClick={onPopUpClose}>{II18.get('title_cancel')}</Button>

          <Button
            id={'btnApplyMobileSignature'}
            onClick={() => onDone(signatureData, touchData)}
            raised
            fill
            disabled={!signatureData}
          >
            {II18.get('title_apply')}
          </Button>
        </div>
      </Page>
    </Popup>
  );
}

SignOnMobilePopUp.propTypes = {
  opened: PropTypes.any,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
  documentName: PropTypes.string,
  widgetTitle: PropTypes.string,
  collectBioData: PropTypes.bool
};
